import type { CSSProperties } from 'react';

export type FixedTypeaheadPositionOptions = {
  minWidth?: number;
  maxWidth?: number;
  /** Soft preference for opening below when this much space exists. */
  preferBelowPx?: number;
  /** Cap on dropdown max-height. */
  maxHeightCap?: number;
  gap?: number;
  zIndex?: number;
};

/**
 * Viewport-fixed dropdown geometry for typeaheads.
 * Accounts for iOS visualViewport offset (keyboard / pinch-zoom) so lists
 * stay anchored under the input even inside transformed / backdrop-filter modals
 * when the list is portaled to document.body.
 */
export function computeFixedTypeaheadStyle(
  rect: DOMRect,
  opts: FixedTypeaheadPositionOptions = {},
): CSSProperties {
  const vv = window.visualViewport;
  const offsetTop = vv?.offsetTop ?? 0;
  const offsetLeft = vv?.offsetLeft ?? 0;
  const viewHeight = vv?.height ?? window.innerHeight;
  const viewWidth = vv?.width ?? window.innerWidth;
  const gap = opts.gap ?? 4;
  const zIndex = opts.zIndex ?? 4200;
  const preferBelowPx = opts.preferBelowPx ?? 160;
  const maxHeightCap = opts.maxHeightCap ?? 320;

  // rect is visual-viewport-relative; viewHeight is visual viewport height.
  const spaceBelow = viewHeight - rect.bottom - 12;
  const spaceAbove = rect.top - 12;
  const preferBelow = spaceBelow >= preferBelowPx || spaceBelow >= spaceAbove;
  const maxHeight = Math.max(140, Math.min(maxHeightCap, preferBelow ? spaceBelow : spaceAbove));

  const minWidth = opts.minWidth ?? 280;
  const maxWidth = Math.min(viewWidth - 24, opts.maxWidth ?? 480);
  const width = Math.min(maxWidth, Math.max(minWidth, rect.width));

  let left = rect.left + offsetLeft;
  if (left + width > offsetLeft + viewWidth - 12) {
    left = Math.max(offsetLeft + 12, offsetLeft + viewWidth - 12 - width);
  }
  if (left < offsetLeft + 12) left = offsetLeft + 12;

  return {
    position: 'fixed',
    left,
    width,
    maxHeight,
    zIndex,
    ...(preferBelow
      ? { top: rect.bottom + offsetTop + gap, bottom: 'auto' }
      : { bottom: window.innerHeight - (rect.top + offsetTop) + gap, top: 'auto' }),
  };
}

/** Reposition listeners for open typeahead lists (scroll, resize, visualViewport). */
export function bindTypeaheadReposition(onReposition: () => void): () => void {
  window.addEventListener('resize', onReposition);
  window.addEventListener('scroll', onReposition, true);
  const vv = window.visualViewport;
  vv?.addEventListener('resize', onReposition);
  vv?.addEventListener('scroll', onReposition);
  return () => {
    window.removeEventListener('resize', onReposition);
    window.removeEventListener('scroll', onReposition, true);
    vv?.removeEventListener('resize', onReposition);
    vv?.removeEventListener('scroll', onReposition);
  };
}
